import React, { useMemo, useState } from 'react';
import { X, LayoutTemplate, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer,
} from 'recharts';
import './TemplateAnalyticsModal.css';

const TEAL  = '#1D9E75';
const BLUE  = '#185FA5';
const RED   = '#E24B4A';
const AMBER = '#EF9F27';

const fmtPct = (n) => `${(parseFloat(n) || 0).toFixed(1)}%`;
const fmtInt = (n) => (parseInt(n) || 0).toLocaleString();

const TplTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="tpl-tt">
      {label && <p className="tpl-tt-label">{label}</p>}
      {payload.map((p, i) => (
        <div key={i} className="tpl-tt-row">
          <span className="tpl-tt-dot" style={{ background: p.fill || p.color }} />
          <span>{p.name}: <strong>{(p.value ?? 0).toLocaleString()}</strong></span>
        </div>
      ))}
    </div>
  );
};

const PER_PAGE = 8;

const TemplateAnalyticsModal = ({ data, loading, onClose }) => {
  const inventory   = data?.inventory          || {};
  const usage       = data?.usage              || {};
  const catPerf     = data?.categoryPerformance || [];
  const topPerf     = data?.topPerformers       || [];
  const tplPerf     = data?.templatePerformance || [];
  const monthly     = data?.monthlyTrend        || [];

  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(tplPerf.length / PER_PAGE));
  const pagedTpl = tplPerf.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  /* Monthly trend bar data */
  const trendData = useMemo(() =>
    monthly.map(m => ({
      name:     m.monthLabel.split(' ')[0],
      Created:  m.created,
      Approved: m.approved,
      Rejected: m.rejected,
    })),
  [monthly]);

  const hasTrend = trendData.some(m => m.Created > 0 || m.Approved > 0);

  /* Category bar items */
  const catBarItems = useMemo(() => [
    { name: 'Marketing',      value: inventory.byCategory?.MARKETING      || 0, color: AMBER },
    { name: 'Utility',        value: inventory.byCategory?.UTILITY        || 0, color: TEAL  },
    { name: 'Authentication', value: inventory.byCategory?.AUTHENTICATION || 0, color: BLUE  },
  ], [inventory]);
  const catBarSum = catBarItems.reduce((a, b) => a + b.value, 0);

  return (
    <div className="tpl-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="tpl-modal" role="dialog" aria-modal="true">

        {/* ── Header ── */}
        <div className="tpl-hdr">
          <div className="tpl-hdr-left">
            <div className="tpl-hdr-badge"><LayoutTemplate size={16} /></div>
            <div>
              <h2 className="tpl-hdr-title">Template Analytics</h2>
              <p className="tpl-hdr-sub">Inventory, usage & performance insights</p>
            </div>
          </div>
          <button className="tpl-hdr-close" onClick={onClose} aria-label="Close"><X size={16} /></button>
        </div>

        {/* ── Body ── */}
        {loading ? (
          <div className="tpl-loading">
            <div className="tpl-spin" />
            <p>Loading analytics…</p>
          </div>
        ) : (
          <div className="tpl-body">

            {/* Section 1 — Inventory Overview */}
            <div className="tpl-section">
              <p className="tpl-section-label">Inventory Overview</p>
              <div className="tpl-stat-grid">
                {[
                  { label: 'Total Templates',  value: fmtInt(inventory.total),                   accent: null  },
                  { label: 'Approved',         value: fmtInt(inventory.byStatus?.approved),       accent: TEAL  },
                  { label: 'Pending',          value: fmtInt(inventory.byStatus?.pending),        accent: AMBER },
                  { label: 'Rejected',         value: fmtInt(inventory.byStatus?.rejected),       accent: RED   },
                  { label: 'Used in Campaigns', value: fmtInt(usage.totalUsedInCampaigns),        accent: BLUE  },
                  { label: 'Never Used',       value: fmtInt(usage.neverUsed),                    accent: null  },
                ].map(s => (
                  <div key={s.label} className="tpl-stat-card">
                    <span className="tpl-stat-val" style={s.accent ? { color: s.accent } : {}}>{s.value}</span>
                    <span className="tpl-stat-lbl">{s.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Section 2 — Category Breakdown */}
            <div className="tpl-section">
              <p className="tpl-section-label">Category Breakdown</p>
              <div className="tpl-breakdown-row">

                {/* Horizontal bars */}
                <div className="tpl-card tpl-hbar-card">
                  <p className="tpl-card-title">Templates by Category</p>
                  <div className="tpl-hbars">
                    {catBarItems.map(item => {
                      const pct = catBarSum > 0 ? Math.round((item.value / catBarSum) * 100) : 0;
                      return (
                        <div key={item.name} className="tpl-hbar-row">
                          <span className="tpl-hbar-lbl">{item.name}</span>
                          <div className="tpl-hbar-track">
                            <div className="tpl-hbar-fill" style={{ width: `${pct}%`, background: item.color }} />
                          </div>
                          <span className="tpl-hbar-val">{item.value}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Category performance cards */}
                <div className="tpl-cat-perf-col">
                  {catPerf.map(c => (
                    <div key={c.category} className="tpl-cat-card">
                      <div className="tpl-cat-header">
                        <span className="tpl-cat-badge" style={{
                          background: c.category === 'MARKETING' ? '#fff8e8' : c.category === 'UTILITY' ? '#edfcf5' : '#eff6ff',
                          color:      c.category === 'MARKETING' ? AMBER     : c.category === 'UTILITY' ? TEAL      : BLUE,
                          borderColor: c.category === 'MARKETING' ? 'rgba(239,159,39,.25)' : c.category === 'UTILITY' ? 'rgba(29,158,117,.25)' : 'rgba(24,95,165,.25)',
                        }}>{c.category}</span>
                        <span className="tpl-cat-meta">{c.totalCampaigns} campaigns · {fmtInt(c.totalRecipients)} recipients</span>
                      </div>
                      <div className="tpl-cat-rates">
                        <span style={{ color: TEAL }}>Del {fmtPct(c.avgDeliveryRate)}</span>
                        <span style={{ color: BLUE }}>Read {fmtPct(c.avgReadRate)}</span>
                        <span style={{ color: RED }}>Fail {fmtPct(c.avgFailureRate)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Section 3 — Monthly Trend */}
            {hasTrend && (
              <div className="tpl-section">
                <p className="tpl-section-label">Monthly Trend</p>
                <div className="tpl-card tpl-chart-card">
                  <ResponsiveContainer width="100%" height={190}>
                    <BarChart data={trendData} barSize={14} barGap={3}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--tpl-grid)" />
                      <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'var(--tpl-muted)' }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: 'var(--tpl-muted)' }} axisLine={false} tickLine={false} allowDecimals={false} />
                      <Tooltip content={<TplTooltip />} />
                      <Bar dataKey="Created"  fill={BLUE}  radius={[3,3,0,0]} name="Created"  />
                      <Bar dataKey="Approved" fill={TEAL}  radius={[3,3,0,0]} name="Approved" />
                      <Bar dataKey="Rejected" fill={RED}   radius={[3,3,0,0]} name="Rejected" />
                    </BarChart>
                  </ResponsiveContainer>
                  <div className="tpl-chart-legend">
                    {[
                      { label: 'Created',  color: BLUE  },
                      { label: 'Approved', color: TEAL  },
                      { label: 'Rejected', color: RED   },
                    ].map(l => (
                      <span key={l.label} className="tpl-legend-item">
                        <span className="tpl-legend-dot" style={{ background: l.color }} />
                        {l.label}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Section 4 — Top Performers */}
            {topPerf.length > 0 && (
              <div className="tpl-section">
                <p className="tpl-section-label">Top Performers</p>
                <div className="tpl-card tpl-table-card">
                  <table className="tpl-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Template</th>
                        <th>Category</th>
                        <th>Campaigns</th>
                        <th>Recipients</th>
                        <th>Delivery</th>
                        <th>Read</th>
                        <th>Failure</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topPerf.map((t, i) => (
                        <tr key={t.templateId || i}>
                          <td className="tpl-tbl-num">{i + 1}</td>
                          <td className="tpl-tbl-name">{t.templateName}</td>
                          <td><span className="tpl-badge" data-cat={t.category}>{t.category}</span></td>
                          <td>{fmtInt(t.campaignCount)}</td>
                          <td>{fmtInt(t.totalRecipients)}</td>
                          <td style={{ color: TEAL }}>{fmtPct(t.deliveryRate)}</td>
                          <td style={{ color: BLUE }}>{fmtPct(t.readRate)}</td>
                          <td style={{ color: t.failureRate > 0 ? RED : 'var(--tpl-muted)' }}>{fmtPct(t.failureRate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Section 5 — All Templates */}
            {tplPerf.length > 0 && (
              <div className="tpl-section">
                <p className="tpl-section-label">All Templates</p>
                <div className="tpl-card tpl-table-card">
                  <table className="tpl-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Template</th>
                        <th>Category</th>
                        <th>Status</th>
                        <th>Campaigns</th>
                        <th>Recipients</th>
                        <th>Delivered</th>
                        <th>Seen</th>
                        <th>Failed</th>
                        <th>Del %</th>
                        <th>Read %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagedTpl.map((t, i) => (
                        <tr key={t.templateId || i}>
                          <td className="tpl-tbl-num">{(page - 1) * PER_PAGE + i + 1}</td>
                          <td className="tpl-tbl-name">{t.templateName}</td>
                          <td><span className="tpl-badge" data-cat={t.category}>{t.category}</span></td>
                          <td><span className="tpl-status">{t.templateStatus}</span></td>
                          <td>{fmtInt(t.campaignCount)}</td>
                          <td>{fmtInt(t.totalRecipients)}</td>
                          <td style={{ color: TEAL }}>{fmtInt(t.delivered)}</td>
                          <td style={{ color: BLUE }}>{fmtInt(t.seen)}</td>
                          <td style={{ color: t.failed > 0 ? RED : 'var(--tpl-muted)' }}>{fmtInt(t.failed)}</td>
                          <td style={{ color: TEAL }}>{fmtPct(t.deliveryRate)}</td>
                          <td style={{ color: BLUE }}>{fmtPct(t.readRate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {totalPages > 1 && (
                    <div className="tpl-pagination">
                      <span className="tpl-pg-info">{tplPerf.length} templates</span>
                      <div className="tpl-pg-controls">
                        <button className="tpl-pg-btn" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft size={14} /></button>
                        <span className="tpl-pg-text">{page} / {totalPages}</span>
                        <button className="tpl-pg-btn" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}><ChevronRight size={14} /></button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

          </div>
        )}
      </div>
    </div>
  );
};

export default TemplateAnalyticsModal;
